import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { ROUTE_PATH } from '../../routes/route-paths';
import { usePeerDetailLite } from '../../hooks/api/usePeers';
import { useBrowseExperiences } from '../../hooks/api/useBrowseExperiences';
import { useTagGroups } from '../../hooks/api/useTagGroups';
import { Header } from '../../components/layout/header';
import { Footer } from '../../components/layout/footer';
import { PeerDetailsHero } from '../../components/peer-details-hero';
import { VideoExperienceTile } from '../../components/video-experience-tile';
import { AudioExperienceTile } from '../../components/audio-experience-tile';
import { ContentSection } from '../../components/content-section';
import { InlineSpinner } from '../../components/common/spinner';
import { Experience, Content } from '../../hooks/api/useExperiences';
import useAuth from '../../hooks/utils/useAuth';
import { HowCallWorksModal } from '../../components/modals/how-call-works-modal';
import { SchedulingModal } from '../../components/modals/scheduling-modal/scheduling-modal';
import { CallDetailsModal } from '../../components/modals/call-details-modal/call-details-modal';
import { useCallRequests } from '../../hooks/api/useCalls';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { TopicFilterDropdown } from '../../components/topic-filter-dropdown';
import { usePeersonaAccess } from '../../hooks/utils/usePeersonaAccess';

/**
 * Prefer media_type; fall back to video_id when media_type is absent (KINDLY-1280).
 * VIDEO rows without a video_id stay out of the Video bucket — VideoExperienceTile
 * returns null without one, so those rows would vanish and still count toward
 * itemsShown. They land in Audio instead (playable via file) rather than disappearing.
 */
const isVideoExperience = (exp: Experience | Content) => {
  const mediaType = 'media_type' in exp ? exp.media_type : undefined;
  if (mediaType === 'AUDIO') return false;
  // Require video_id — VideoExperienceTile returns null without one.
  return Boolean(exp.video_id);
};

export const PeerDetailsPage: React.FC = () => {
  const INITIAL_VIDEO_ITEMS = 12;
  const INITIAL_AUDIO_ITEMS = 4;
  const LOAD_MORE_VIDEO_ITEMS = 12;
  const LOAD_MORE_AUDIO_ITEMS = 8;

  const location = useLocation();
  const history = useHistory();
  const { user } = useAuth();
  const dfMdMedia = useMediaQuery('md');
  const isUserSuspended = user?.status === 'suspended';

  const { getTagGroupById } = useTagGroups(true);
  const searchParams = new URLSearchParams(location.search);
  const listenerId: number = Number(searchParams.get('listenerId'));
  const { data: peerDetails, isLoading: peerLoading } = usePeerDetailLite(listenerId);
  const { isPeerPeersonaEnabled } = usePeersonaAccess();
  const peersonaChatbotId =
    peerDetails && isPeerPeersonaEnabled(peerDetails.id, peerDetails.chatbot_id)
      ? (peerDetails.chatbot_id ?? null)
      : null;
  const { data: topicTagGroups } = useTagGroups();
  const [scheduleModal, setScheduleModal] = useState(false);
  const [howCallWorksModal, setHowCallWorksModal] = useState(false);
  const [startOnFeelingStep, setStartOnFeelingStep] = useState<'Now' | 'Time' | undefined>();
  const [initialTime, setInitialTime] = useState<moment.Moment | undefined>(undefined);
  const [selectedTagGroupId, setSelectedTagGroupId] = useState<number | undefined>(undefined);
  const [videoItemsShown, setVideoItemsShown] = useState(INITIAL_VIDEO_ITEMS);
  const [audioItemsShown, setAudioItemsShown] = useState(INITIAL_AUDIO_ITEMS);
  const [isCallDetailsModalOpen, setIsCallDetailsModalOpen] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState<number | undefined>(undefined);
  const [initialCallRequestData, setInitialCallRequestData] = useState<any>(undefined);
  const [pendingReopenCallId, setPendingReopenCallId] = useState<number | undefined>(undefined);
  const [pendingReopenActiveCallId, setPendingReopenActiveCallId] = useState<number | undefined>(undefined);
  const [pendingReopenInitialCallRequestData, setPendingReopenInitialCallRequestData] = useState<any>(undefined);
  const [reopenWithActiveCallId, setReopenWithActiveCallId] = useState<number | undefined>(undefined);

  const { data: callRequests } = useCallRequests(user?.caller_role_id);

  // Fetch experiences using the query hook (similar to browse-experiences page)
  const { data: experiencesData, isLoading: isLoadingExperiences } = useBrowseExperiences(
    selectedTagGroupId,
    undefined,
    undefined,
    undefined,
    undefined,
    1,
    100,
    listenerId,
  );

  // Separate video and audio by media_type (KINDLY-1280). Fall back to video_id
  // when media_type is absent. VIDEO without video_id stays in Audio so the tile
  // remains visible (VideoExperienceTile cannot render without a Vimeo id).
  const videoExperiences = useMemo(
    () => experiencesData?.data.filter((exp) => isVideoExperience(exp as Experience | Content)) || [],
    [experiencesData?.data],
  );

  const audioExperiences = useMemo(
    () => experiencesData?.data.filter((exp) => !isVideoExperience(exp as Experience | Content)) || [],
    [experiencesData?.data],
  );

  // Get selected tag group name for filtering
  const selectedTagGroup = useMemo(
    () => (selectedTagGroupId ? getTagGroupById(selectedTagGroupId) : undefined),
    [selectedTagGroupId, getTagGroupById],
  );

  // Note: Tag group filtering is now handled server-side by useBrowseExperiences
  // So filteredVideoExperiences and filteredAudioExperiences are the same as videoExperiences and audioExperiences
  const filteredVideoExperiences = videoExperiences;
  const filteredAudioExperiences = audioExperiences;

  // Reset items shown when filter changes
  useEffect(() => {
    setVideoItemsShown(INITIAL_VIDEO_ITEMS);
    setAudioItemsShown(INITIAL_AUDIO_ITEMS);
  }, [selectedTagGroupId]);

  const loadMoreVideos = useCallback(() => {
    if (filteredVideoExperiences.length > videoItemsShown) {
      setVideoItemsShown((prev) => Math.min(prev + LOAD_MORE_VIDEO_ITEMS, filteredVideoExperiences.length));
    }
  }, [filteredVideoExperiences.length, videoItemsShown]);

  const loadMoreAudio = useCallback(() => {
    if (filteredAudioExperiences.length > audioItemsShown) {
      setAudioItemsShown((prev) => Math.min(prev + LOAD_MORE_AUDIO_ITEMS, filteredAudioExperiences.length));
    }
  }, [filteredAudioExperiences.length, audioItemsShown]);

  // Calculate the next available time as a Moment object for passing to the modal
  // TODO: Combine this with the nextAvailableTime function below
  const nextAvailableTimeMoment = useMemo(() => {
    if (peerDetails?.is_available) {
      return null; // Will use 'Now' instead
    }

    if (!peerDetails?.availabilities || peerDetails.availabilities.length === 0) {
      return null;
    }

    const userTimeZone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = moment().tz(userTimeZone);

    // Find the next available time slot from recurring weekly availabilities
    let nextAvailable: moment.Moment | null = null;

    for (const availability of peerDetails.availabilities) {
      if (!availability.start_date || !availability.starts_at) continue;

      // Parse the reference date to get the day of week
      const referenceDate = moment(availability.start_date).tz(userTimeZone);
      const timeStr = availability.starts_at; // Format: "17:00:00" or "17:00"
      const timeParts = timeStr.split(':');
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1] || '0', 10);

      // Get the day of week (0 = Sunday, 1 = Monday, etc.)
      const dayOfWeek = referenceDate.day();

      // Calculate the next occurrence of this day/time
      let nextOccurrence = now.clone().day(dayOfWeek).hour(hours).minute(minutes).second(0).millisecond(0);

      // If this week's occurrence has passed or is exactly now, get next week's
      if (nextOccurrence.isBefore(now) || nextOccurrence.isSame(now)) {
        nextOccurrence.add(7, 'days');
      }

      // Track the earliest next availability
      if (!nextAvailable || nextOccurrence.isBefore(nextAvailable)) {
        nextAvailable = nextOccurrence;
      }
    }

    return nextAvailable;
  }, [peerDetails?.is_available, peerDetails?.availabilities, user?.timezone]);

  const handleScheduleClick = useCallback(() => {
    // When "Connect Now" is clicked, start directly on feeling step
    // When next available time is shown, also start on feeling step with that time
    if (peerDetails?.is_available) {
      setStartOnFeelingStep('Now');
      setInitialTime(undefined);
    } else {
      setStartOnFeelingStep('Time');
      setInitialTime(nextAvailableTimeMoment || undefined);
    }
    setScheduleModal(true);
  }, [peerDetails?.is_available, nextAvailableTimeMoment]);

  const handleSeeMoreTimesClick = useCallback(() => {
    // When "See more times" is clicked, start on time step
    setStartOnFeelingStep(undefined);
    setScheduleModal(true);
  }, []);

  const handleHowDoesCallWorkClick = useCallback(() => {
    setHowCallWorksModal(true);
  }, []);

  const handleSeeMoreListenersClick = useCallback(() => {
    history.push(ROUTE_PATH.BROWSE_PEER_LISTENERS);
  }, [history]);

  const handleScheduleModalClose = useCallback((context?: 'scheduled' | 'cancelled' | null, callRequestData?: any) => {
    setScheduleModal(false);
    setStartOnFeelingStep(undefined);
    setInitialTime(undefined);

    // If a call was scheduled, open the call details modal for that call
    if (context === 'scheduled' && callRequestData) {
      // Extract the call request ID from the response
      // The response structure is: { response: CreateCallRequestResponse, metadata: {...} }
      // So callRequestData should be the CreateCallRequestResponse which has an id field
      const callRequestId = callRequestData?.id;
      if (callRequestId) {
        setSelectedCallId(callRequestId);
        setInitialCallRequestData(callRequestData);
        setIsCallDetailsModalOpen(true);
      }
    }
  }, []);

  const nextAvailableTime = useMemo(() => {
    if (peerDetails?.is_available) {
      return 'Now';
    }

    // This should never happen, but just in case
    if (!peerDetails?.availabilities || peerDetails.availabilities.length === 0) {
      return 'No times available';
    }

    const userTimeZone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = moment().tz(userTimeZone);

    // Find the next available time slot from recurring weekly availabilities
    let nextAvailable: moment.Moment | null = null;

    for (const availability of peerDetails.availabilities) {
      if (!availability.start_date || !availability.starts_at) continue;

      // Parse the reference date to get the day of week
      const referenceDate = moment(availability.start_date).tz(userTimeZone);
      const timeStr = availability.starts_at; // Format: "17:00:00" or "17:00"
      const timeParts = timeStr.split(':');
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1] || '0', 10);

      // Get the day of week (0 = Sunday, 1 = Monday, etc.)
      const dayOfWeek = referenceDate.day();

      // Calculate the next occurrence of this day/time
      let nextOccurrence = now.clone().day(dayOfWeek).hour(hours).minute(minutes).second(0).millisecond(0);

      // If this week's occurrence has passed or is exactly now, get next week's
      if (nextOccurrence.isBefore(now) || nextOccurrence.isSame(now)) {
        nextOccurrence.add(7, 'days');
      }

      // Track the earliest next availability
      if (!nextAvailable || nextOccurrence.isBefore(nextAvailable)) {
        nextAvailable = nextOccurrence;
      }
    }

    if (!nextAvailable) {
      return 'No times available';
    }

    // Format time as "3pm" or "7:30am"
    // Use 'h' format (1-12) instead of 'hh' (01-12) to avoid leading zeros
    let timeStr = nextAvailable.format('h:mma').toLowerCase();
    // Remove ":00" when minutes are zero (e.g., "3:00pm" -> "3pm")
    timeStr = timeStr.replace(':00', '');
    // Remove leading zero from hour if present (e.g., "07:30am" -> "7:30am")
    const formattedTime = timeStr.replace(/^0(\d)/, '$1');

    // Check if it's today
    const isToday = nextAvailable.isSame(now, 'day');

    if (isToday) {
      return `${formattedTime} today`;
    } else {
      // Format day name as lowercase (e.g., "monday")
      const dayName = nextAvailable.format('dddd').toLowerCase();
      return `${formattedTime} ${dayName}`;
    }
  }, [peerDetails?.is_available, peerDetails?.availabilities, user?.timezone]);

  if (peerLoading || isLoadingExperiences) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <InlineSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  if (!peerDetails) {
    return (
      <div className="flex min-h-screen flex-col bg-white">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <p className="text-center text-gray-500">Peer not found</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <div className="flex min-h-screen flex-col bg-white">
        <Header />
        <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
          <PeerDetailsHero
            peerDetails={peerDetails}
            onScheduleClick={handleScheduleClick}
            onSeeMoreTimesClick={handleSeeMoreTimesClick}
            onHowDoesCallWorkClick={handleHowDoesCallWorkClick}
            onSeeMoreListenersClick={handleSeeMoreListenersClick}
            nextAvailableTime={nextAvailableTime}
          />

          {/* Explore Experiences Section */}
          <div className="max-w-page mx-auto px-6 py-8 md:py-12">
            <div className="mb-6 md:mb-8">
              <h2 className="font-albert-sans mb-4 text-center text-xl font-semibold leading-[30px] tracking-[-0.2px] text-black md:mb-6 md:text-left md:text-[28px] md:leading-[42px] md:tracking-[-0.28px]">
                Explore {peerDetails.user.display_name}'s experiences
              </h2>

              <div className="mb-6 flex justify-center md:mb-8 md:justify-start">
                <TopicFilterDropdown
                  label="Filter by Topic"
                  placeholder="Filter by Topic"
                  selectedTagGroupId={selectedTagGroupId}
                  onSelect={(value) => setSelectedTagGroupId(value as number | undefined)}
                  includeAllOption={true}
                  topicTagGroups={topicTagGroups}
                />
              </div>
            </div>

            <ContentSection
              title="Video stories"
              items={filteredVideoExperiences}
              itemsShown={videoItemsShown}
              onLoadMore={loadMoreVideos}
              shouldShow={filteredVideoExperiences.length > 0}
              renderItem={(experience) => (
                <VideoExperienceTile
                  key={experience?.id}
                  experience={experience as Experience | Content | undefined}
                  handleScheduleModalOpen={handleScheduleClick}
                  selectedTagGroupName={selectedTagGroup?.name}
                />
              )}
            />

            <ContentSection
              title="Audio stories"
              items={filteredAudioExperiences}
              itemsShown={audioItemsShown}
              onLoadMore={loadMoreAudio}
              shouldShow={filteredAudioExperiences.length > 0}
              renderItem={(experience) => (
                <AudioExperienceTile
                  key={experience?.id}
                  experience={experience as Experience | Content | undefined}
                  selectedTagGroupName={selectedTagGroup?.name}
                  peersonaChatbotId={peersonaChatbotId}
                />
              )}
            />

            {filteredVideoExperiences.length === 0 && filteredAudioExperiences.length === 0 && (
              <div className="px-4 text-center text-gray-500 md:px-0">
                <p className="text-base md:text-lg">No experiences found.</p>
                {selectedTagGroupId && <p className="mt-2 text-sm">Try adjusting your filter.</p>}
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>

      {scheduleModal && peerDetails && !isUserSuspended && (
        <>
          <SchedulingModal
            open={scheduleModal}
            onExit={handleScheduleModalClose}
            listenerId={peerDetails.id}
            listenerAvailableNow={peerDetails.available}
            availabilities={peerDetails.availabilities || []}
            listenerName={peerDetails.user.display_name}
            listenerImage={peerDetails.profile_photo_url_square}
            location="peer_details"
            startOnFeelingStep={startOnFeelingStep}
            initialTime={initialTime}
          />
        </>
      )}

      <HowCallWorksModal
        open={howCallWorksModal}
        onClose={() => {
          setHowCallWorksModal(false);
          if (pendingReopenCallId !== undefined || pendingReopenActiveCallId !== undefined) {
            setSelectedCallId(pendingReopenCallId);
            setReopenWithActiveCallId(pendingReopenActiveCallId);
            setInitialCallRequestData(pendingReopenInitialCallRequestData);
            setIsCallDetailsModalOpen(true);
            setPendingReopenCallId(undefined);
            setPendingReopenActiveCallId(undefined);
            setPendingReopenInitialCallRequestData(undefined);
          }
        }}
      />

      <CallDetailsModal
        open={isCallDetailsModalOpen}
        onClose={() => {
          setIsCallDetailsModalOpen(false);
          setSelectedCallId(undefined);
          setInitialCallRequestData(undefined);
          setReopenWithActiveCallId(undefined);
        }}
        callRequests={callRequests || []}
        initialSelectedCallId={selectedCallId}
        initialSelectedActiveCallId={reopenWithActiveCallId}
        initialCallRequestData={initialCallRequestData}
        onOpenHowCallWorks={(context) => {
          setPendingReopenCallId(context.selectedCallId);
          setPendingReopenActiveCallId(context.selectedActiveCallId);
          setPendingReopenInitialCallRequestData(initialCallRequestData);
          setIsCallDetailsModalOpen(false);
          setSelectedCallId(undefined);
          setInitialCallRequestData(undefined);
          setHowCallWorksModal(true);
        }}
      />
    </>
  );
};
